import CustomerIdRequest from "api/sharedModels/CustomerIdRequest";
import { EventTypeRequest } from "./../api/thorRequestModels/Events/EventTypeRequest";
import { ReturnToStockRequest } from "api/thorRequestModels/Events/ReturnToStockRequest";
import { ProductQuantityRequest } from "./../api/thorRequestModels/Events/ProductQuantityRequest";
import { PageRequest } from "api/sharedModels/PageRequest";
import { AssignAssessmentRequest } from "./../api/thorRequestModels/Events/AssignAssessmentRequest";
import { AssignUserRequest } from "api/thorRequestModels/Events/AssignUserRequest";
import { services } from "api/serviceConfig";
import { BatchFlowRequest } from "api/thorRequestModels/Events/BatchFlowRequest";
import { BatchRequest } from "api/thorRequestModels/Events/BatchRequest";
import { BatchStatusAuthRequest } from "api/thorRequestModels/Events/BatchStatusRequest";
import { EventPriorityRequest } from "api/thorRequestModels/Events/EventPriorityRequest";
import { EventRequest, ReOpenEventRequest } from "api/thorRequestModels/Events/EventRequest";
import { makeAutoObservable } from "mobx";
import { ActiveEvent, EventType } from "models/thorEventModels/ActiveEvents";
import { BatchProductFlow } from "models/thorEventModels/BatchProductFlow";
import { EventDetails, EventStatus } from "models/thorEventModels/EventDetails";
import { EventSummary } from "models/thorEventModels/EventSummary";
import { AssessmentStatus } from "models/thorEventModels/BatchImpactAssessment";
import { AssigneeType } from "models/users/AssignType";
import { EventAttachment } from "models/thorEventModels/EventAttachment";
import { EventDeviceData } from "models/thorEventModels/DeviceData";
import { getColorByIndex } from "helpers/getColorByIndex";
import { ChangeDueDateRequest } from "api/thorRequestModels/Events/ChangeDueDateRequest";

export class ThorEventViewStore {
    constructor() {
        makeAutoObservable(this);
    }

    activeEvents: ActiveEvent[] = [];
    currentEventPage = 1;
    totalEventPages = 1;
    isEventsLoaded = false;

    isAssignMenuOpen = false;

    eventDetails: EventDetails = {
        eventId: 0,
        priority: "",
        eventCreatedAt: new Date(),
        minTemperature: 0,
        maxTemperature: 0,
        durationInHours: 0,
        impactedDeliveries: [],
        impactedOrders: [],
        impactedHandlingUnits: [],
        impactedBatches: [],
        isClosed: false,
        eventType: EventType.transportation,
        eventClosedAt: new Date(),
        reviewerUserId: "",
        qaUserId: "",
        eventStatusId: EventStatus.New,
        eventAttachments: [],
        dueDate: new Date(),
    };

    attachedFiles: EventAttachment[] = [];

    isFileAttachModalOpen = false;
    isFileViewModalOpen = false;

    isEventClosed = false;

    isEventDetailsLoaded = false;

    isPrioritySet = false;
    isTypeSet = false;
    IsReturnedToStockSet = false;

    eventSummary: EventSummary = {
        eventId: 0,
        batchSummaries: [],
    };

    isEventSummaryLoaded = false;

    productFlows: BatchProductFlow[] = [];
    selectedFlow: string = "";
    isFlowSelected = false;

    isFlowsLoaded = false;

    selectedBatch = 0;

    currentBucket: string = "";

    selectedHistoryEvent = 0;

    isAssessmentSet = false;
    isQuantitySet = false;

    selectedDevise: string = "";

    deviceData: EventDeviceData = {
        temperatureUpperLimit: 0,
        temperatureLowerLimit: 0,
        deviceData: [],
    };

    isDeviceDataLoaded = false;

    get isLastEventPage() {
        return this.currentEventPage === this.totalEventPages;
    }

    setSelectedDevise = (label: string) => {
        this.selectedDevise = label;
    };

    setFileAttachModalOpen = (open: boolean) => {
        this.isFileAttachModalOpen = open;
    };

    setFileViewModalOpen = (open: boolean) => {
        this.isFileViewModalOpen = open;
    };

    setAttachedFiles = (files: EventAttachment[]) => {
        this.attachedFiles = files;
    };

    setAssignMenuOpen = (isOpen: boolean) => {
        this.isAssignMenuOpen = isOpen;
    };

    setSelectedHistoryEvent = (eventId: number) => {
        this.selectedHistoryEvent = eventId;
    };

    setCurrentBucket = (bucket: string) => {
        this.currentBucket = bucket;
    };

    setSelectedBatch = (batchId: number) => {
        this.selectedBatch = batchId;
    };

    setEventPage = (page: number) => {
        this.currentEventPage = page;
    };

    clearEventPagination = () => {
        this.activeEvents = [];
        this.currentEventPage = 1;
        this.totalEventPages = 1;
    };

    moveToEventPage = (direction: "back" | "forward") => {
        direction === "back" ? this.currentEventPage-- : this.currentEventPage++;
    };

    loadActiveEvents = async (request: PageRequest) => {
        this.isEventsLoaded = true;
        try {
            const response = await services.Thor.getActiveEvents(request);
            this.activeEvents = response.data.items;
            this.totalEventPages = response.data.totalPages;
        } catch (error) {
            console.log(error);
        } finally {
            this.isEventsLoaded = false;
        }
    };

    loadEventDetails = async (request: EventRequest) => {
        this.isEventDetailsLoaded = true;
        try {
            const response = await services.Thor.getEventDetails(request);
            this.eventDetails = response.data;
            this.attachedFiles = response.data.eventAttachments ?? [];
        } catch (error) {
            console.log(error);
        } finally {
            this.isEventDetailsLoaded = false;
        }
    };

    closeEventProcess = async (request: BatchStatusAuthRequest) => {
        const impactedBatch = this.eventDetails.impactedBatches.find((x) => x.batchId === request.batchId);
        impactedBatch!.details.decision = request.isApproved ? "Approved" : "Rejected";

        if (impactedBatch!.assessmentStatusId === AssessmentStatus.ReAssessing) {
            impactedBatch!.assessmentStatusId--;
        } else {
            impactedBatch!.assessmentStatusId++;
        }

        await this.getEventSummary({
            eventId: request.eventId,
            customerId: request.customerId,
        });

        if (!this.eventDetails.impactedBatches.some((x) => x.details.decision === "Pending")) {
            await this.closeEvent({
                eventId: request.eventId,
                customerId: request.customerId,
            });
            if (
                this.eventDetails.impactedBatches.every(
                    (x) => x.assessmentStatusId === AssessmentStatus.EvaluationCompleted
                )
            ) {
                this.eventDetails.eventStatusId = EventStatus.Closed;
            }
        }
    };

    setBatchStatus = async (request: BatchStatusAuthRequest) => {
        let response;
        try {
            response = await services.Thor.setBatchStatus(request);
            if (response.status === 204) {
                await this.closeEventProcess(request);
            }
        } catch (error) {
            console.log("Error: ", error);
        } finally {
            request.callback(response ? response.status : 0);
        }
    };

    closeEvent = async (request: EventRequest) => {
        this.isEventClosed = true;
        try {
            const response = await services.Thor.setEventStatus(request);
            if (response.status === 204) {
                this.eventDetails.isClosed = true;
            }
        } catch (error) {
            console.log(error);
        } finally {
            this.isEventClosed = false;
        }
    };

    exportEventPDF = async (request: EventRequest) => {
        try {
            await services.Thor.downloadEventPDF(request).then((d) => {
                const blob = new Blob([d.data], {
                    type: "pdf",
                });
                const fileURL = window.URL.createObjectURL(blob);
                const tempLink = document.createElement("a");
                tempLink.href = fileURL;
                tempLink.setAttribute("download", request.eventId + ".pdf");
                tempLink.click();
            });
        } catch (error) {
            console.log(error);
        }
    };

    getProductFlows = async (request: BatchRequest) => {
        this.isFlowsLoaded = true;
        try {
            const response = await services.Thor.getProductsFlow(request);
            this.productFlows = response.data;
        } catch (error) {
            console.log(error);
        } finally {
            this.isFlowsLoaded = false;
        }
    };

    setProductFlow = async (request: BatchFlowRequest) => {
        this.isFlowSelected = true;
        try {
            const response = await services.Thor.setProductFlow(request);
            if (response.status === 204) {
                this.selectedFlow = this.productFlows.find((x) => x.flowId === request.flowId)!.flowId.toString();
                this.loadEventDetails({
                    eventId: this.eventDetails.eventId,
                    customerId: request.customerId,
                });
            }
        } catch (error) {
            console.log(error);
        } finally {
            this.isFlowSelected = false;
        }
    };

    getEventSummary = async (request: EventRequest) => {
        this.isEventSummaryLoaded = true;
        try {
            const response = await services.Thor.getEventSummary(request);
            this.eventSummary = response.data;
        } catch (error) {
            console.log(error);
        } finally {
            this.isEventSummaryLoaded = false;
        }
    };

    setEventPriority = async (request: EventPriorityRequest) => {
        this.isPrioritySet = true;
        try {
            const response = await services.Thor.setEventPriority(request);
            if (response.status === 204) {
                this.eventDetails.priority = request.priority;
            }
        } catch (error) {
            console.log(error);
        } finally {
            this.isPrioritySet = false;
        }
    };

    assignUserToEvent = async (request: AssignUserRequest) => {
        try {
            const response = await services.Thor.assignUserToEvent(request);
            if (response.status === 204) {
                if (this.activeEvents.length === 0) {
                    if (request.assignUserType === AssigneeType.Reviewer) {
                        this.eventDetails.reviewerUserId = request.userId;
                        this.eventDetails.eventStatusId = EventStatus.PendingReview;
                    } else {
                        this.eventDetails.qaUserId = request.userId;
                        this.eventDetails.eventStatusId = EventStatus.PendingApproval;
                    }
                } else {
                    if (request.assignUserType === AssigneeType.Reviewer) {
                        this.activeEvents.find((x) => x.eventId === request.eventId)!.reviewerUserId = request.userId;
                        this.activeEvents.find((x) => x.eventId === request.eventId)!.eventStatusId =
                            EventStatus.PendingReview;

                        this.eventDetails.reviewerUserId = request.userId;
                        this.eventDetails.eventStatusId = EventStatus.PendingReview;
                    } else {
                        this.activeEvents.find((x) => x.eventId === request.eventId)!.qaUserId = request.userId;
                        this.activeEvents.find((x) => x.eventId === request.eventId)!.eventStatusId =
                            EventStatus.PendingApproval;

                        this.eventDetails.qaUserId = request.userId;
                        this.eventDetails.eventStatusId = EventStatus.PendingApproval;
                    }
                }
            }
        } catch (error) {
            console.log(error);
        } finally {
            this.setAssignMenuOpen(false);
        }
    };

    setAssessmentStatus = async (request: AssignAssessmentRequest) => {
        this.isAssessmentSet = true;
        try {
            const response = await services.Thor.setAssessmentStatus(request);
            if (response.status === 204) {
                this.eventDetails.impactedBatches.find((x) => x.batchId === request.batchId)!.assessmentStatusId++;
                if (
                    this.eventDetails.impactedBatches.every(
                        (x) => x.assessmentStatusId === AssessmentStatus.PendingDecision
                    )
                ) {
                    this.eventDetails.eventStatusId = EventStatus.PendingApproval;
                }
            }
        } catch (error) {
            console.log(error);
        } finally {
            this.isAssessmentSet = false;
        }
    };

    setProductQuantity = async (request: ProductQuantityRequest) => {
        this.isQuantitySet = true;
        try {
            const response = await services.Thor.setProductQuantity(request);
            if (response.status === 204) {
                this.eventDetails.impactedBatches.find(
                    (x) => x.transportationUnitId === request.transportationUnitId
                )!.quantity = request.quantity;
            }
        } catch (error) {
            console.log(error);
        } finally {
            this.isQuantitySet = false;
        }
    };

    returnToStock = async (request: ReturnToStockRequest) => {
        this.IsReturnedToStockSet = true;
        try {
            const response = await services.Thor.returnToStock(request);
            if (response.status === 204) {
                this.eventDetails.impactedBatches.find((x) => x.batchId === request.batchId)!.isReturnedToStock =
                    request.decision;
            }
        } catch (error) {
            console.log(error);
        } finally {
            this.IsReturnedToStockSet = false;
        }
    };

    setEventType = async (request: EventTypeRequest) => {
        this.isTypeSet = true;
        try {
            const response = await services.Thor.setEventType(request);
            if (response.status === 204) {
                this.eventDetails.eventType = request.eventType;
            }
        } catch (error) {
            console.log(error);
        } finally {
            this.isTypeSet = false;
        }
    };

    loadDeviceData = async (request: EventRequest) => {
        this.isDeviceDataLoaded = true;

        try {
            const response = await services.Thor.getEventMonitorsData(request);

            if (response !== undefined) {
                this.deviceData = response.data;
                for (let index = 0; index < this.deviceData.deviceData.length; index++) {
                    this.deviceData.deviceData[index].borderColor = getColorByIndex(index);
                    this.deviceData.deviceData[index].backgroundColor = getColorByIndex(index);
                    this.deviceData.deviceData[index].borderWidth = 2;
                }
            }
        } catch (error) {
            console.log(error);
        } finally {
            this.isDeviceDataLoaded = false;
        }
    };

    changeDueDate = async (request: ChangeDueDateRequest) => {
        try {
            await services.Thor.changeDueDate(request);
        } catch (error) {
            console.log(error);
        }
    };

    reopenEvent = async (request: ReOpenEventRequest) => {
        try {
            const response = await services.Thor.reopenEvent(request);
            if (response.status === 204) {
                this.eventDetails.eventStatusId = EventStatus.ReOpened;
                this.eventDetails.qaUserId = request.userEmail;
                this.eventDetails.reviewerUserId = request.userEmail;
            }
        } catch (error) {
            console.log(error);
        }
    };

    resetAllEvents = async (request: CustomerIdRequest) => {
        await services.Thor.resetAllEvents(request);
    };

    resetEvent = async (request: EventRequest) => {
        await services.Thor.resetEvent(request);
    };
}
